import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ImgUnlock } from '../components/Images/ImgUnlock';
import { ImgMenu } from '../components/Images/ImgMenu';
import { ImgTimes } from '../components/Images/ImgTimes';

import { type DragEndEvent } from '@dnd-kit/core';

const untitledNoteTitle = 'Untitled note';

import { type CategoryInfo } from '../models/CategoryInfo';
import { type NotesResponse } from '../models/NotesResponse';
import { type NoteInfo } from '../models/NoteInfo';
import { PromiseCompletion } from '../classes/PromiseCompletion';

import { apiService } from '../services/ApiService';
import { notificationService } from '../services/NotificationService';
import { ArrayService } from '../services/ArrayService';

const RenameNoteDialog = React.lazy(() => import('./dialogs/RenameNoteDialog'));
import { type RenameNoteDialogProps } from './dialogs/RenameNoteDialog';

const ColorNoteDialog = React.lazy(() => import('./dialogs/ColorNoteDialog'));
import { type ColorNoteDialogProps } from './dialogs/ColorNoteDialog';

const ShareNoteDialog = React.lazy(() => import('./dialogs/ShareNoteDialog'));
import { type ShareNoteDialogProps } from './dialogs/ShareNoteDialog';

const ChangeCategoryDialog = React.lazy(() => import('./dialogs/ChangeCategoryDialog'));
import { type ChangeCategoryDialogProps, type ChangeCategoryNoteParameters } from './dialogs/ChangeCategoryDialog';

const CreateNoteDialog = React.lazy(() => import('./dialogs/CreateNoteDialog'));
import { type CreateNoteDialogProps, type CreateNoteParameters } from './dialogs/CreateNoteDialog';
import Page404 from './Page404';

const ManageCategoriesDialog = React.lazy(() => import('./dialogs/ManageCategoriesDialog'));
import { type ManageCategoriesDialogProps } from './dialogs/ManageCategoriesDialog';
import { type CategoryContext } from '../components/CategoryPicker';
import { ContextMenuItem, ContextMenuSeparator } from '../components/ContextMenu';
import { ModalButtonType, modalService } from '../components/Modal';
import Editor from '../components/Editor';
import { extensions } from '@services/Extensions';
import NoteSidebar, { type NoteMatch } from '../components/NoteSidebar';

@observer
export default class Notes extends React.Component<unknown> {
  @observable private _notes: NoteInfo[] = [];
  @observable private _categories: CategoryInfo[] = [];
  @observable private _activeCategoryId = '';
  @observable private _activeNoteId = '';
  @observable private _filterText = '';
  @observable.ref private _matches: NoteMatch[] | null = null;
  @observable private _isArchive = false;
  @observable private _searchContent = false;
  @observable private _vectorSearch = false;
  @observable private _vectorSearchAvailable = false;
  @observable private _isUnlocked = false;
  @observable private _editorHeaderHeight = 41;
  @observable private _isMobileSidebarVisible = false;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _notesListRef: React.RefObject<HTMLDivElement> = React.createRef();
  private _searchCancellationSource: AbortController;

  constructor(props: unknown){
    super(props);
    makeObservable(this);
    this._searchCancellationSource = new AbortController();
  }

  async componentDidMount(): Promise<void>{
    await this._loadData(true);
    setTimeout(this._recalculateScrollSize, 100);
    window.addEventListener('resize', this._onWindowResize);
    window.addEventListener('blur', this._onBlur);
    void this._loadSearchConfig();
  }

  componentWillUnmount(){
    window.removeEventListener('resize', this._onWindowResize);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('blur', this._onBlur);
    document.body.classList.remove('mobile-sidebar-open');
  }

  render(): JSX.Element{
    const notes = this.Notes.filter(x => (x.name ?? untitledNoteTitle)?.toLowerCase()?.includes(this._filterText.toLowerCase()) !== false || this._matches?.find(m => m.noteId === x.id));
    const sortedNotes = notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);
    const isTouchDevice = this._isTouchDevice();

    const activeNote = this._notes.find(x => x.id === this._activeNoteId);

    return (
      <div className="notes-container">
        {/* Mobile overlay */}
        <div
          className={`mobile-sidebar-overlay${this._isMobileSidebarVisible ? ' visible' : ''}`}
          onClick={this._onToggleMobileSidebar}
        />

        <NoteSidebar
          notes={sortedNotes}
          categories={this._categories}
          filterText={this._filterText}
          matches={this._matches}
          isArchive={this._isArchive}
          searchContent={this._searchContent}
          vectorSearch={this._vectorSearch}
          vectorSearchAvailable={this._vectorSearchAvailable}
          activeCategoryId={this._activeCategoryId}
          activeNoteId={this._activeNoteId}
          isMobileSidebarVisible={this._isMobileSidebarVisible}
          userName={this._userName}
          isTouchDevice={isTouchDevice}
          containerRef={this._notesListRef}
          onFilterChange={this._onFilterChange}
          onSearchContentToggle={this._onSearchContentToggle}
          onVectorSearchToggle={this._onVectorSearchToggle}
          onArchiveToggle={this._onArchiveToggle}
          onCategoryChanged={this._onCategoryChanged}
          onCategorySettingsClick={this._onCategorySettingsClick}
          onNoteMouseDown={this._onNoteMouseDown}
          onAddNote={this._addNewNote}
          onDragEnd={this._handleDragEnd}
          onRenderContextMenu={this._renderMenu}
        />

        {/* Mobile sidebar toggle button */}
        <button
          className="mobile-sidebar-toggle"
          onClick={this._onToggleMobileSidebar}
          title={this._isMobileSidebarVisible ? 'Close menu' : 'Open menu'}
        >
          {this._isMobileSidebarVisible ? <ImgTimes size={18} /> : <ImgMenu size={18} />}
        </button>
        
        <div className="note-area">
          <style>
            {`#snow-container .ql-container {  height: calc(100vh - ${this._editorHeaderHeight}px) !important; height: calc(var(--vh, 1vh) * 100 - ${this._editorHeaderHeight}px)!important; }`}
          </style>
          {!!this._activeNoteId && <Editor 
            key={this._activeNoteId} 
            noteId={this._activeNoteId} 
            notFoundView={Page404} 
            sharedView={false}
            onDataLoaded={() => setTimeout(this._onWindowResize, 0)}
          />}
          {activeNote?.isProtected && !this._isUnlocked && <div className="protected-screen">
            <button className="btn btn-success" onClick={this._onUnlockClick}>
              Unlock <ImgUnlock size={16} />
            </button>
          </div>}
        </div>
      </div>
    );
  }
  
  private _renderMenu = (element: HTMLElement): JSX.Element | null => {
    const activityCodeTarget = element.closest('.note-item') as HTMLDivElement;
    if (activityCodeTarget){
      const id = activityCodeTarget.dataset.id as string;
      const note = this._notes.find(x => x.id === id);

      return (
        <>
          <ContextMenuItem title="Rename note" onClick={() => this._renameNote(id)} />
          <ContextMenuItem title="Share" onClick={() => this._shareNote(id)} />
          <ContextMenuItem title="Color" onClick={() => this._colorNote(id)} />
          <ContextMenuItem title={note?.isProtected ? "Unprotect" : "Protect"} onClick={() => this._toggleProtected(id, !!note?.isProtected)} />
          <ContextMenuSeparator/>
          <ContextMenuItem title="Category" onClick={() => this._moveToCategory(id)} />
          {!note?.isArchived && <ContextMenuItem title="Archive" onClick={() => this._archiveNote(id)} />}
          {note?.isArchived && <ContextMenuItem title="Undo Archive" onClick={() => this._undoArchiveNote(id)} />}
          <ContextMenuSeparator/>
          <ContextMenuItem title="Delete note" onClick={() => this._deleteNote(id)} />
        </>
      );
    }

    return null;
  }

  private _isTouchDevice() {
    return (('ontouchstart' in window) ||  (navigator.maxTouchPoints > 0));
  }

  @computed
  private get Notes() {
    const isFullFilter = this._filterText && this._filterText.startsWith('@');
    if (isFullFilter){
      return this._notes;
    }
    return this._notes.filter(x => (x.categoryId ?? '') === (this._activeCategoryId ?? ''));
  }

  @computed
  private get _userName(): string {
    return window.user?.name ?? window.user?.email ?? 'Guest';
  }

  private _onWindowResize = () => {
    this._recalculateScrollSize();
  }

  @action
  private _recalculateScrollSize = () => {
    const header = document.getElementsByClassName('ql-toolbar')[0];
    this._editorHeaderHeight = header?.getBoundingClientRect()?.height || 41;
  }

  @action.bound
  private _onFilterChange(text: string): void {
    this._filterText = text;
    if (text) {
      this._startSearch();
    } else {
      this._matches = null;
    }
  }

  @action.bound
  private async _onCategorySettingsClick(context: CategoryContext){
    context.close();

    await modalService.show<ManageCategoriesDialogProps, string>(ManageCategoriesDialog, {
      onRefresh: () => this._refreshCategories()
    });
  }
  
  @action.bound
  private _onNoteMouseDown(e: React.MouseEvent, note: NoteInfo){
    if (e.button === 1 /* Middle */) {
      e.preventDefault();
      e.stopPropagation();
      window.open('/?noteId=' + note.id , '_blank')?.focus();
      return;
    }

    const isFilterActive = !!this._filterText;
    // if filter active - select also category of the note
    if (isFilterActive && note.categoryId !== this._activeCategoryId){
      this._activeCategoryId = note.categoryId ?? '';
    }
    
    this._activeNoteId = note.id;
    this._isUnlocked = false;
    this._setTitle(note.name);
    
    const isRightClick = e.button === 2;

    // Close mobile sidebar when selecting a note
    // check for .context-menu-target as target and ignore if click is on it, to allow opening context menu without closing sidebar
    if (!isRightClick && window.innerWidth <= 768 && !((e.target as HTMLElement).closest('.context-menu-target'))) {
      this._isMobileSidebarVisible = false;
    }
  }

  @action.bound
  private _onToggleMobileSidebar(){
    this._isMobileSidebarVisible = !this._isMobileSidebarVisible;
    
    // Prevent body scrolling when sidebar is open on mobile
    if (window.innerWidth <= 768) {
      if (this._isMobileSidebarVisible) {
        document.body.classList.add('mobile-sidebar-open');
      } else {
        document.body.classList.remove('mobile-sidebar-open');
      }
    }
  }

  @action.bound
  private _onUnlockClick(){
    this._isUnlocked = true;
  }

  @action.bound
  private _onVisibilityChange(){
    if (document.visibilityState == 'hidden') {
      this._isUnlocked = false;
    }
  }

  @action.bound
  private _onBlur(){
    this._isUnlocked = false;
  }

  @action.bound
  private async _onArchiveToggle(){
    if (this._loader.isPending) return;

    this._isArchive = !this._isArchive;
    await this._loadData(false);
    if (this._filterText && this._searchContent){
      this._performSearch();
    }
  }

  @action.bound
  private async _onCategoryChanged(e: CategoryInfo){
    this._activeCategoryId = e.id;
  }

  @action.bound
  private async _onSearchContentToggle(){
    this._searchContent = !this._searchContent;
    if (this._searchContent){
      this._vectorSearch = false;
      this._performSearch();
    } else {
      this._matches = null;
    }
  }

  @action.bound
  private async _onVectorSearchToggle(){
    this._vectorSearch = !this._vectorSearch;
    if (this._vectorSearch){
      this._searchContent = false;
      this._performSearch();
    } else {
      this._matches = null;
    }
  }
  
  private async _loadData(init: boolean): Promise<void>{
    const parameters = { 
      isArchive: init ? null : (this._isArchive ? 'true' : 'false') 
    };

    const notesResponse = await apiService.get<NotesResponse>('/api/note', parameters, {
      loader: this._loader
    });

    runInAction(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const openNoteId = urlParams.get('noteId');
      const qsNoteId = !!notesResponse.notes.find(x => x.id === openNoteId) && openNoteId ? openNoteId : null;
      if (qsNoteId){
        urlParams.delete('noteId');
        window.history.replaceState({}, document.title, window.location.pathname + urlParams.toString());      
      }

      this._activeCategoryId = notesResponse.activeCategoryId ?? '';

      const categories = notesResponse.categories;
      if (notesResponse.notes.find(x => !x.categoryId)){
        categories.push({
          id: '',
          name: 'Default',
          sortOrder: 0
        });
      }
      this._categories = categories;
      this._notes = notesResponse.notes;
      this._activeNoteId = qsNoteId ?? notesResponse.activeNoteId ?? '';
      
      if (init){
        this._isArchive = !!notesResponse.activeNoteArchived;
        const activeNote = notesResponse.notes.find(x => x.id === notesResponse.activeNoteId);
        if (activeNote){
          this._setTitle(activeNote.name);
        }
      }
    });
  }

  private async _refreshCategories(){
    const categories = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });

    runInAction(() => {
      this._categories = categories;
    });
  }

  private _setTitle(noteName: string){
    document.title = 'Notes - ' + noteName;
  }

  @action.bound
  private async _addNewNote(): Promise<void>{

    const { button, result } = await modalService.show<CreateNoteDialogProps, CreateNoteParameters>(CreateNoteDialog as unknown as React.FunctionComponent<CreateNoteDialogProps>, {});
    if (!result || button !== ModalButtonType.Create){
      return;
    }

    const newNote = await apiService.post<NoteInfo>('/api/note', { ...result, categoryId: this._activeCategoryId }, {
      loader: this._loader
    });

    this._notes.unshift(newNote);
    this._activeNoteId = newNote.id;
    this._setTitle(newNote.name);
  }

  @action.bound
  private async _deleteNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation("Confirmation", `Do you really want to delete note (${note.name ?? untitledNoteTitle})`, { color: 'danger', buttonTitle: 'Remove' })){
      return;
    }

    await apiService.delete('/api/note/' + id, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(n => n.id === id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
    }
    const activeNote = this._notes[0];
    if (activeNote){
      this._setTitle(activeNote.name);
    }
    this._activeNoteId = activeNote?.id;
  }

  @action.bound
  private async _renameNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<RenameNoteDialogProps, string>(RenameNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put('/api/note/' + id + '/name', { name: result }, {
      loader: this._loader
    });

    note.name = result;
  }
  
  @action.bound
  private async _colorNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<ColorNoteDialogProps, string>(ColorNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put<NoteInfo>('/api/note/' + id + '/color', { color: result }, {
      loader: this._loader
    });

    note.color = result;
  }

  @action.bound
  private async _shareNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    await modalService.show<ShareNoteDialogProps, string>(ShareNoteDialog, {
      note: note,
      onUpdate: (e) => {
        note.isPrivate = !e.shareId;
        note.shareId = e.shareId;
        note.shareAsReadOnly = e.isReadOnly;
      }
    });

  }

  @action.bound
  private async _archiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to archive note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/archive', null, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      const activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
  }

  @action.bound
  private async _moveToCategory(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    const { button, result } = await modalService.show<ChangeCategoryDialogProps, ChangeCategoryNoteParameters>(ChangeCategoryDialog, {
      note: note
    });
    
    if (button === ModalButtonType.Create && result){
      note.categoryId = result.id;
      this._activeCategoryId = result.id;
    }
  }

  @action.bound
  private async _toggleProtected(id: string, isProtected: boolean): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to ${isProtected ? 'unprotect' : 'protect'} note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/toggle-protect', null, {
      loader: this._loader
    });

    note.isProtected = !note.isProtected;
  }

  @action.bound
  private async _undoArchiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to activate note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/undo-archive', null, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      const activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
  }

  @action.bound
  private async _handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const notes = this.Notes.filter(x => (x.name ?? untitledNoteTitle)?.toLowerCase()?.includes(this._filterText.toLowerCase()) !== false || this._matches?.find(m => m.noteId === x.id));
    const sortedNotes = notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);

    const oldIndex = sortedNotes.findIndex(n => n.id === active.id);
    const newIndex = sortedNotes.findIndex(n => n.id === over.id);

    ArrayService.arrayMoveMutable(sortedNotes, oldIndex, newIndex);
    sortedNotes.forEach((w, index) => w.sortOrder = index);

    const allSortedNotes = this._notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);
    const orderModel = {
      ids: allSortedNotes.map(w => w.id)
    };
    await apiService.put('/api/note/reorder', orderModel);
    notificationService.success('Order was saved!');
  }

  private _startSearch(){
    if (!this._filterText){
      this._matches = null;
      return;
    }
    extensions.executeTimeout(this._performSearch, 300);
  }

  private _cancelSearch(): void{
    this._searchCancellationSource.abort();
    this._searchCancellationSource = new AbortController();
  }

  private _performSearch = async () => {
    this._cancelSearch();

    if (!this._filterText){
      return;
    }

    const searchText = this._filterText.startsWith('@') ? this._filterText.substring(1) : this._filterText;

    if (this._vectorSearch) {
      const { results } = await apiService.post<{ results: Array<{ noteId: string; score: number; content: string }> }>(
        '/api/note/vector-search',
        { text: searchText },
        { cancellationToken: this._searchCancellationSource.signal, loader: this._loader }
      );
      this._matches = results.map(r => ({ noteId: r.noteId, parts: [r.content] }));
      return;
    }

    if (!this._searchContent){
      return;
    }

    const { matches } = await apiService.post<{text: string, matches: NoteMatch[]}>('/api/note/search', { text: searchText, isArchive: this._isArchive ? 'true' : '' }, {
      cancellationToken: this._searchCancellationSource.signal,
      loader: this._loader
    });

    this._matches = matches;
  }

  private async _loadSearchConfig(): Promise<void> {
    const cfg = await apiService.get<{ qdrantConfigured: boolean; embeddingConfigured: boolean }>('/api/note/search-config');
    runInAction(() => {
      this._vectorSearchAvailable = cfg.qdrantConfigured && cfg.embeddingConfigured;
    });
  }
}